import { useState, type ButtonHTMLAttributes, type ReactNode } from "react";

import { getCopy } from "@mobile/app/config/appCopy";

import { LANGUAGE_META, type LearnerSetup } from "../appModel";
import "./contentHeading.css";

interface HeadingProps {
  title: string;
  supportTitle?: string;
  setup: LearnerSetup;
}

function useHeadingTranslation({ title, supportTitle, setup }: HeadingProps) {
  const identity = JSON.stringify([title, supportTitle, setup.targetLanguage, setup.supportLanguage]);
  const [translatedIdentity, setTranslatedIdentity] = useState<string | null>(null);
  const translated = translatedIdentity === identity;
  const support = supportTitle?.trim();
  const canTranslate = setup.targetLanguage !== setup.supportLanguage && Boolean(support && support !== title.trim());
  const language = translated && canTranslate ? setup.supportLanguage : setup.targetLanguage;
  const copy = getCopy(setup.supportLanguage);
  const flagLanguage = translated ? setup.targetLanguage : setup.supportLanguage;
  const flag = canTranslate ? (
    <button
      type="button"
      className="content-heading-flag"
      aria-label={translated ? copy.hideTranslation : copy.translateAction}
      title={translated ? copy.hideTranslation : copy.translateAction}
      aria-expanded={translated}
      onClick={(event) => {
        event.stopPropagation();
        setTranslatedIdentity(translated ? null : identity);
      }}
    >
      <img className="language-flag-image" src={LANGUAGE_META[flagLanguage].flagImage} width={28} height={21} alt="" />
    </button>
  ) : null;
  return { text: translated && canTranslate && support ? support : title, language, flag };
}

export function ContentHeading(props: HeadingProps) {
  const { text, language, flag } = useHeadingTranslation(props);
  return <span className="content-heading"><span lang={language} dir="auto">{text}</span>{flag}</span>;
}

export function ContentHeadingChoice(props: HeadingProps & {
  buttonProps: ButtonHTMLAttributes<HTMLButtonElement>;
  children: (text: string, language: string) => ReactNode;
}) {
  const { text, language, flag } = useHeadingTranslation(props);
  return (
    <div className={`content-heading-choice${flag ? " has-heading-translation" : ""}`}>
      <button type="button" {...props.buttonProps}>{props.children(text, language)}</button>
      {flag}
    </div>
  );
}
